/**
 * 진행 단계 (순수): 회기마다 자료 입력 → 회기 처리 → 전사 확인 → AI 분석 → 검토·승인 → 기록지 승인,
 * 그 다음 내담자 문서(사례보고서: 만들기 → AI로 빈 칸 채우기 → 승인).
 * 화면은 이것 하나로 "지금 어디까지 했고, 다음에 무엇을 누르면 되는지"를 보여준다.
 */
import type { DocumentArtifact, Session } from '../schema/v1';

export type StepState = 'done' | 'current' | 'todo' | 'missed';
export type Step = { key: string; label: string; state: StepState; path: string; action: string };

const DOC_BLANK = /\[(상담자 직접 작성 필요|자료 부족)/;

/** pending: 이 회기에서 아직 승인하지 않은 것(위험·목표 진행·사례개념화·진단 개정안)의 수 */
export function sessionSteps(s: Session, pending = 0): Step[] {
  const p = s.processingState;
  const base = `/sessions/${s.id}`;
  const hasInput = p.filesSaved || !!s.samsungTranscriptId || !!s.appleTranscriptId;
  const processed = s.status !== 'draft' && s.status !== 'processing';
  const verified = processed && s.status !== 'verification';
  const approved = s.status === 'approved';
  const raw: { key: string; label: string; done: boolean; path: string; action: string }[] = [
    {
      key: 'input',
      label: '자료 입력',
      done: hasInput || processed,
      path: `${base}/run?step=1`,
      action: '전사·메모 올리기',
    },
    { key: 'process', label: '회기 처리', done: processed, path: `${base}/run?step=2`, action: '회기 처리하기' },
    { key: 'verify', label: '전사 확인', done: verified, path: `${base}/run?step=2`, action: '중요 전사 확인하기' },
    {
      key: 'ai',
      label: 'AI 분석',
      done: p.aiAnalysisImported,
      path: `${base}/run?step=3`,
      action: 'AI 분석하기 (외부)',
    },
    {
      key: 'review',
      label: '검토·승인',
      done: processed && pending === 0 && (p.aiAnalysisImported || approved),
      path: `${base}/run?step=4`,
      action: pending ? `검토·승인 (${pending}개)` : '검토·승인',
    },
    {
      key: 'journal',
      label: '기록지 승인',
      done: approved,
      path: `${base}/run?step=5`,
      action: '상담기록지 검토·승인',
    },
  ];
  let currentSet = false;
  return raw.map((r, i) => {
    let state: StepState;
    if (r.done) state = 'done';
    else if (raw.slice(i + 1).some((x) => x.done))
      state = 'missed'; // 뒤 단계는 했는데 이 단계는 빠짐
    else if (!currentSet) {
      state = 'current';
      currentSet = true;
    } else state = 'todo';
    return { key: r.key, label: r.label, state, path: r.path, action: r.action };
  });
}

export type ClientProgress = {
  sessions: { session: Session; steps: Step[] }[];
  documents: Step[];
  /** 지금 누르면 되는 것 하나 (없으면 모두 끝) */
  next: { label: string; path: string } | null;
};

export function clientProgress(input: {
  clientId: string;
  sessions: Session[];
  documents: DocumentArtifact[];
  terminated: boolean;
  /** 회기별 아직 승인하지 않은 것의 수 */
  pendingReview?: Record<string, number>;
}): ClientProgress {
  const sessions = [...input.sessions]
    .filter((s) => !s.deletedAt)
    .sort((a, b) => a.sessionNumber - b.sessionNumber)
    .map((session) => ({ session, steps: sessionSteps(session, input.pendingReview?.[session.id] ?? 0) }));
  const docsPath = `/clients/${input.clientId}/documents`;
  const docType = input.terminated ? 'termination_report' : 'case_report';
  const docName = input.terminated ? '종결보고서' : '사례보고서';
  const doc = input.documents
    .filter((d) => !d.deletedAt && d.documentType === docType)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .at(-1);
  const docPath = doc ? `${docsPath}?doc=${doc.id}` : docsPath;
  const rawDocs = [
    { key: 'doc-make', label: `${docName} 만들기`, done: !!doc, path: docsPath, action: `${docName} 만들기` },
    {
      key: 'doc-ai',
      label: 'AI로 빈 칸 채우기',
      done: !!doc && (doc.status === 'approved' || !DOC_BLANK.test(doc.content)),
      path: docPath,
      action: `${docName} 빈 칸 AI로 채우기`,
    },
    {
      key: 'doc-approve',
      label: '확인·승인',
      done: doc?.status === 'approved',
      path: docPath,
      action: `${docName} 확인·승인`,
    },
  ];
  // 회기가 남아 있으면 문서 단계는 '지금 할 단계'로 표시하지 않는다 (회기부터)
  const sessionsPending = sessions.some(({ steps }) => steps.some((x) => x.state !== 'done'));
  let currentSet = sessionsPending;
  const documents: Step[] = rawDocs.map((r) => {
    let state: StepState = r.done ? 'done' : 'todo';
    if (!r.done && !currentSet) {
      state = 'current';
      currentSet = true;
    }
    return { key: r.key, label: r.label, state, path: r.path, action: r.action };
  });

  let next: ClientProgress['next'] = null;
  for (const { session, steps } of sessions) {
    const st = steps.find((x) => x.state === 'missed' || x.state === 'current');
    if (st) {
      next = { label: `${session.sessionNumber}회기 · ${st.action}`, path: st.path };
      break;
    }
  }
  // 회기를 마쳤으면 다음 회기로 (진행 중인 사례). 문서 단계는 막대·문서 버튼에서. 종결된 사례는 문서로
  if (!next && !input.terminated) next = { label: '다음 회기 시작', path: `/clients/${input.clientId}/run` };
  if (!next && sessions.length) {
    const st = documents.find((x) => x.state !== 'done');
    if (st) next = { label: st.action, path: st.path };
  }
  return { sessions, documents, next };
}

/** 진행률과 놓친 단계 수 (진행 막대·홈 카드 공통). 문서는 필요할 때 만드는 것이라 넣지 않는다 */
export function progressSummary(p: ClientProgress): { pct: number; missed: number } {
  const all = p.sessions.flatMap((x) => x.steps);
  const done = all.filter((s) => s.state === 'done').length;
  return {
    pct: all.length ? Math.round((done / all.length) * 100) : 0,
    missed: all.filter((s) => s.state === 'missed').length,
  };
}
